import React from "react";
import { CssBaseline } from "@mui/material";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import RequireAuth from "./features/auth/components/RequireAuth";
import SignedOut from "./features/auth/pages/SignedOut"; 

import MeterChanges from "./features/meterChanges/pages/MeterChange/MeterChanges";
import ContentLayout from "./layouts/ContentLayout";
import CreateConfig from "./features/configCreate/pages/CreateConfig/CreateConfig";
import DataQuality from "./features/dataQuality/pages/DataQuality/DataQuality";
import Home from "./features/home/pages/Home/Home";

import Unit from "./features/units/pages/Unit/Unit";
import UnitDetails from "./features/units/pages/UnitDetails/UnitDetails";
import VersionDetails from "./features/units/pages/UnitVersions/UnitVersion";

import Operator from "./features/operator/pages/Operator/Operator";

import Meter from "./features/meters/pages/Meter/Meter";
import MeterDetails from "./features/meters/pages/MeterDetails/MeterDetails";
import MeterVersion from "./features/meters/pages/MeterVersion/MeterVersion";

import IncomingFiles from "./features/incomingFiles/pages/IncomingFiles/IncomingFiles";

import ExportFile from "./features/exportFiles/pages/ExportFile/ExportFile";

import EVN from "./features/evns/pages/EVN/EVN";
import EVNDetails from "./features/evns/pages/EVNDetails/EVNDetails";
import EVNVersion from "./features/evns/pages/EVNVersion/EVNVersion";

import StructureLog from "./features/structureLogs/pages/StructureLog/StructureLog";

import OperatorExport from "./features/operatorExport/pages/OperatorExport/OperatorExport";
import ExportUnitExclusions from "./features/exportUnitExclusions/pages/ExportUnitExclusions/ExportUnitExclusions";

import { TrailProvider } from "./layouts/TrailProvider";



function App() {
  const runtimeMaintenanceMode = window.__ENV__?.maintenanceMode;
  const isMaintenanceMode = ["true"].includes(
    (runtimeMaintenanceMode || "").toLowerCase()
  );

  if (isMaintenanceMode && window.location.pathname !== "/maintenance.html") {
    try {
      // Prefer SPA navigation if available
      if (window && window.location) {
        window.location.replace('/maintenance.html');
      } else {
        // fallback
        window.location.href = '/maintenance.html';
      }
    } catch (e) {
      // last resort
      window.location.href = '/maintenance.html';
    }
    return null;
  }

  return (
    <>
      <CssBaseline />
      <Router>
        {/* Wrap the routed content with TrailProvider */}
        <TrailProvider>
          <Routes>
            
            {/* Redirect root to /home */}
            <Route path="/" element={<Navigate to="/home" replace />} />

            {/* Public signed-out page */}
            <Route path="/signed-out" element={<SignedOut />} />

            {/* Protected /home/* routes */}
            <Route
              path="/home/*"
              element={
                <RequireAuth>
                  <ContentLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Home />} />
              <Route path="data-quality" element={<DataQuality />} />
              <Route path="operator-export-summary" element={<OperatorExport />} />
              <Route path="export-unit-exclusions" element={<ExportUnitExclusions />} />

              {/* Units */}
              <Route path="units" element={<Unit />} />
              <Route path="units/:unitName" element={<UnitDetails />} />
              <Route path="units/:unitName/history" element={<VersionDetails />} />

              {/* Operators */}
              <Route path="operator" element={<Operator />} />

              {/* Meters */}
              <Route path="meters" element={<Meter />} />
              <Route path="meters/:meterNumber" element={<MeterDetails />} />
              <Route path="meters/:meterNumber/history" element={<MeterVersion />} />

              {/* Meter Changes */}
              <Route path="meter-changes" element={<MeterChanges />} />

              {/* Create config */}
              <Route path="create-config" element={<CreateConfig />} />

              {/* Incoming logs */}
              <Route path="incoming-logs" element={<IncomingFiles />} />

              {/* Export logs */}
              <Route path="export-logs" element={<ExportFile />} />

              {/* Structure logs */}
              <Route path="structure-logs" element={<StructureLog />} />

              {/* EVNs */}
              <Route path="evns" element={<EVN />} />
              <Route path="evns/:evnId" element={<EVNDetails />} />
              <Route path="evns/:evn/history" element={<EVNVersion />} />
            </Route>

            {/* catch-all to keep users inside the app */}
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </TrailProvider>
      </Router>
    </>
  );
}

export default App;
